/* ──────────────────────────────────────────────────────────────
   src/constants/enums.ts
   Catálogos y utilidades compartidas (versión dinámica)
   ────────────────────────────────────────────────────────────── */

export type EstadoCategoria = 'OK' | 'CANCEL' | 'PENDIENTE' | 'BLOQUEO' | 'INFO';
export type ActorCancelacion = 'ALUMNO' | 'PROFESOR' | 'SISTEMA' | 'OTRO';

export interface EstadoReservaItem {
  code: string;
  descripcion: string;
  bloqueante: boolean;
  cuentaHoras: boolean;
  requiereComentario: boolean;
  visibleCliente: boolean;
  color: string | null;
  orden: number;
  categoria: EstadoCategoria;
  actorCancelacion?: ActorCancelacion | null;
  systemFlag?: boolean;
}

/* ──────────────────────────────────────────────────────────────
   Estado del módulo (catálogo dinámico + helpers)
   ────────────────────────────────────────────────────────────── */

const CATALOGO_ESTADOS: EstadoReservaItem[] = [];

/** Listas “compatibles” para no romper imports antiguos
 *  ⚠️ OJO: exportamos arrays **const** y siempre mutamos con .splice()
 *  para mantener la misma referencia en todos los consumidores.
 */
export const ESTADOS: string[] = [];
export const ESTADOS_RESERVA: string[] = [];
export const ESTADOS_CANCELLED: string[] = [];

/** Pub/Sub muy ligero para notificar cambios del catálogo */
type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribeEstados(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emitEstadosUpdate() {
  for (const fn of listeners) fn();
}

/** Copia del catálogo completo */
export function getEstadosReservaCatalog(): EstadoReservaItem[] {
  return [...CATALOGO_ESTADOS];
}

/** Resolve por code o por label */
export function resolveEstado(input?: string): EstadoReservaItem | undefined {
  if (!input) return undefined;
  return (
    CATALOGO_ESTADOS.find((e) => e.code === input) ||
    CATALOGO_ESTADOS.find((e) => e.descripcion === input)
  );
}

/** Utilidades de conversión */
export function getEstadoLabel(input?: string): string {
  return resolveEstado(input)?.descripcion ?? input ?? '';
}
export function getEstadoCode(input?: string): string {
  return resolveEstado(input)?.code ?? input ?? '';
}
export function isEstadoCancel(input?: string): boolean {
  const e = resolveEstado(input);
  return e ? e.categoria === 'CANCEL' : false;
}
export function requiresComentario(input?: string): boolean {
  const e = resolveEstado(input);
  return e ? !!e.requiereComentario || e.categoria === 'CANCEL' : false;
}

/**
 * Setter principal del catálogo (normaliza y recalcula derivados).
 * Lo debes llamar tras hacer GET /estados-reserva.
 * Mutamos **in-place** para preservar referencias exportadas.
 */
export function setEstadosReservaCatalog(list: any[]): void {
  const norm: EstadoReservaItem[] = list.map((r: any) => ({
    code: r.code,
    descripcion: r.descripcion,
    bloqueante: Boolean(r.bloqueante ?? r.bloqueante === 1),
    cuentaHoras: Boolean(r.cuenta_horas ?? r.cuentaHoras ?? r.cuenta_horas === 1),
    requiereComentario: Boolean(
      r.requiere_comentario ?? r.requiereComentario ?? r.requiere_comentario === 1,
    ),
    visibleCliente: Boolean(r.visible_cliente ?? r.visibleCliente ?? r.visible_cliente === 1),
    color: r.color ?? null,
    orden: Number(r.orden ?? 100),
    categoria: (r.categoria ?? 'INFO') as EstadoCategoria,
    actorCancelacion: (r.actor_cancelacion ??
      r.actorCancelacion ??
      null) as ActorCancelacion | null,
    systemFlag: r.system_flag ?? r.systemFlag ?? undefined,
  }));

  norm.sort((a, b) => a.orden - b.orden || a.descripcion.localeCompare(b.descripcion));

  // Mutación in-place de todas las estructuras
  CATALOGO_ESTADOS.splice(0, CATALOGO_ESTADOS.length, ...norm);

  const labels = norm.map((e) => e.descripcion);
  ESTADOS.splice(0, ESTADOS.length, ...labels);
  ESTADOS_RESERVA.splice(0, ESTADOS_RESERVA.length, ...labels);
  ESTADOS_CANCELLED.splice(
    0,
    ESTADOS_CANCELLED.length,
    ...norm.filter((e) => e.categoria === 'CANCEL').map((e) => e.descripcion),
  );

  emitEstadosUpdate(); // 🔔 notificar a quien esté suscrito
}

/**
 * Inicializador: lo llamas una vez al arrancar la app (ej. en App.tsx).
 * Carga desde la API y setea el catálogo.
 */
export async function initEstadosReserva(): Promise<void> {
  try {
    const { listEstados } = await import('../api/estadosReserva');
    const rows = await listEstados();
    setEstadosReservaCatalog(rows);
  } catch (err) {
    // Error cargando estados desde API
  }
}

/* ──────────────────────────────────────────────────────────────
   Fallback inicial (por si la app renderiza antes de cargar el catálogo)
   ────────────────────────────────────────────────────────────── */

const FALLBACK: EstadoReservaItem[] = [
  {
    code: 'CONFIRMED',
    descripcion: 'Confirmed',
    bloqueante: true,
    cuentaHoras: true,
    requiereComentario: false,
    visibleCliente: true,
    color: '#4caf50',
    orden: 10,
    categoria: 'OK',
  },
  {
    code: 'PENDING_RESCHEDULE', // ⬅️ alineado con tu BBDD
    descripcion: 'Pendiente de reagendar',
    bloqueante: false,
    cuentaHoras: false,
    requiereComentario: false,
    visibleCliente: true,
    color: '#2196f3',
    orden: 30,
    categoria: 'PENDIENTE',
  },
  {
    code: 'CANCEL_OTHER',
    descripcion: 'Cancelled for other reasons',
    bloqueante: false,
    cuentaHoras: false,
    requiereComentario: true,
    visibleCliente: true,
    color: '#f44336',
    orden: 220,
    categoria: 'CANCEL',
    actorCancelacion: 'OTRO',
  },
];

// Inicializamos con fallback solo si aún no hay datos (evita sobrescribir en HMR)
if (ESTADOS.length === 0) {
  setEstadosReservaCatalog(FALLBACK);
}

/* ────────── Cursos / Year Grade (sin cambios) ────────── */
export const CURSOS = [
  'G12 IB',
  'G11 IB',
  'G9 IB',
  'Y9 GCSE/IGCSE',
  'Y10 GCSE/IGCSE',
  'Y11 GCSE/IGCSE',
  'Y12 AL',
  'Y13 AL',
  'MYP',
  '1 BACH ESPAÑOL',
  '2 BACH ESPAÑOL',
  'Others',
  'APOYO UNIVERSIDAD',
  '1 ESO',
  '2 ESO',
] as const;

/* ────────── Notificaciones y otros catálogos (sin cambios) ────────── */
export const NOTIF_LABELS: Record<string, string> = {
  BOOKING_CREATED: 'notificaciones.event.RESERVA_CREATED',
  BOOKING_UPDATED: 'notificaciones.event.RESERVA_MODIFIED',
  BOOKING_CANCELLED: 'notificaciones.event.RESERVA_CANCELLED',
  LESSON_REM_2H: 'notificaciones.event.LESSON_REM_2H',
  LESSON_DAILY_DIGEST: 'notificaciones.event.LESSON_DAILY_DIGEST',
  PENDING_REBOOK_WEEKLY_DIGEST: 'notificaciones.event.PENDING_REBOOK_WEEKLY_DIGEST',
  PENDING_REBOOK_REMINDER_2D: 'notificaciones.event.PENDING_REBOOK_REMINDER_2D',
  VAC_REQ_CREATED: 'notifications.vacReqCreated',
  VAC_REQ_APPROVED: 'notifications.vacReqApproved',
  VAC_REQ_REJECTED: 'notifications.vacReqRejected',
  VAC_REQ_BATCH_CREATED: 'notifications.vacReqBatchCreated',
  VAC_REQ_BATCH_APPROVED: 'notifications.vacReqBatchApproved',
  VAC_REQ_BATCH_REJECTED: 'notifications.vacReqBatchRejected',
  ABS_REQ_CREATED: 'notifications.absReqCreated',
  ABS_REQ_APPROVED: 'notifications.absReqApproved',
  ABS_REQ_REJECTED: 'notifications.absReqRejected',
  DOC_AVAILABLE: 'notif.docAvailable',
  DOC_PENDING_SIGNATURE: 'notif.docPendingSignature',
  FIRMA_PENDIENTE_ALUMNO: 'notifications.firmaPendienteAlumno',
  BROADCAST: 'notifications.broadcast.title',
  MEETING_SCHEDULED: 'notificaciones.event.MEETING_SCHEDULED',
  MEETING_NEXT_SCHEDULED: 'notificaciones.event.MEETING_NEXT_SCHEDULED',
  MEETING_PENDING_RESCHEDULE: 'notificaciones.event.MEETING_PENDING_RESCHEDULE',
  MEETINGS_DAILY_DIGEST_STAFF: 'notificaciones.event.MEETINGS_DAILY_DIGEST_STAFF',
  MEETING_FOLLOWUP_PROMPT: 'notificaciones.event.MEETING_FOLLOWUP_PROMPT',
};

export const MOTIVOS_AUSENCIA = {
  sick: { id: 1, color: '#4caf50', labelKey: 'absence.sick' },
  maternity: { id: 2, color: '#d81b60', labelKey: 'absence.maternity' },
  wedding: { id: 3, color: '#ff9800', labelKey: 'absence.wedding' },
  family_wedding: {
    id: 4,
    color: '#ffb300',
    labelKey: 'absence.familyWedding',
  },
  serious_issue: { id: 5, color: '#9e9e9e', labelKey: 'absence.seriousIssue' },
  surgery: { id: 6, color: '#3f51b5', labelKey: 'absence.surgery' },
  doctor: { id: 7, color: '#03a9f4', labelKey: 'absence.doctor' },
  other: { id: 8, color: '#607d8b', labelKey: 'absence.other' },
} as const;

export type MotivoSlug = keyof typeof MOTIVOS_AUSENCIA;

export const DIAS_PER_COURSE = 30;
